import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, MessageSquare, Save, ExternalLink, ToggleLeft, ToggleRight } from 'lucide-react';

const AdminFeedbackSettings = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled: false,
    google_url: '',
    tripadvisor_url: ''
  });

  const getAuthHeader = () => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin');
      return null;
    }
    return { 'Authorization': `Basic ${auth}`, 'Content-Type': 'application/json' };
  };

  useEffect(() => {
    const fetchSettings = async () => {
      const headers = getAuthHeader();
      if (!headers) return;

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/feedback-settings`,
          { headers }
        );
        if (!response.ok) throw new Error('Unauthorized');
        const data = await response.json();
        setSettings(data);
      } catch (error) {
        toast({
          title: "Erreur",
          description: "Impossible de charger les paramètres",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSave = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    setSaving(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/feedback-settings`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify(settings)
        }
      );

      if (!response.ok) throw new Error('Failed to save');

      const data = await response.json();
      setSettings(data);
      toast({
        title: "Paramètres enregistrés",
        description: "Les paramètres de feedback ont été mis à jour"
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible d'enregistrer les paramètres",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Link to="/admin/dashboard" className="text-gray-500 hover:text-gray-700">
              <ArrowLeft size={24} />
            </Link>
            <h1 className="text-xl font-bold text-gray-900">Emails de Feedback</h1>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
            <Save size={18} className="mr-2" />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Info Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <MessageSquare className="text-blue-600 mt-0.5" size={20} />
            <div>
              <h3 className="font-semibold text-blue-900">Comment ça fonctionne ?</h3>
              <p className="text-sm text-blue-800 mt-1">
                Un email de feedback est envoyé automatiquement 24 heures après la date et l'heure 
                de la réservation, uniquement pour les réservations avec le statut "Confirmée". 
                Chaque réservation ne reçoit qu'un seul email - jamais de doublon.
              </p>
            </div>
          </div>
        </div>

        {/* Settings Card */}
        <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
          {/* Enable Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <h3 className="font-semibold text-gray-900">Activer les emails de feedback</h3>
              <p className="text-sm text-gray-600 mt-1">
                Envoyer automatiquement un email demandant un avis après chaque visite
              </p>
            </div>
            <button
              onClick={() => setSettings(prev => ({ ...prev, enabled: !prev.enabled }))}
              className={`p-2 rounded-lg transition-colors ${
                settings.enabled ? 'bg-green-100 text-green-600' : 'bg-gray-200 text-gray-500'
              }`}
              data-testid="feedback-toggle"
            >
              {settings.enabled ? (
                <ToggleRight size={32} />
              ) : (
                <ToggleLeft size={32} />
              )}
            </button>
          </div>

          {/* Google URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <span className="flex items-center gap-2">
                Lien Google My Business
                <a 
                  href="https://business.google.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800"
                >
                  <ExternalLink size={14} />
                </a>
              </span>
            </label>
            <input
              type="url"
              value={settings.google_url}
              onChange={(e) => setSettings(prev => ({ ...prev, google_url: e.target.value }))}
              placeholder="https://www.google.com/search?...#lrd=..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="google-url-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Le lien direct vers le formulaire d'avis Google (ouvre directement sur la rédaction d'avis)
            </p>
          </div>

          {/* TripAdvisor URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <span className="flex items-center gap-2">
                Lien TripAdvisor
                <a 
                  href="https://www.tripadvisor.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800"
                >
                  <ExternalLink size={14} />
                </a>
              </span>
            </label>
            <input
              type="url"
              value={settings.tripadvisor_url}
              onChange={(e) => setSettings(prev => ({ ...prev, tripadvisor_url: e.target.value }))}
              placeholder="https://fr.tripadvisor.ch/UserReviewEdit-..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              data-testid="tripadvisor-url-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Le lien direct vers le formulaire d'avis TripAdvisor (ouvre directement sur la rédaction d'avis)
            </p>
          </div>

          {/* Cron Job Info */}
          <div className="border-t pt-6 mt-6">
            <h3 className="font-semibold text-gray-900 mb-3">Configuration technique</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-700 mb-2">
                <strong>Endpoint à appeler :</strong>
              </p>
              <code className="block bg-gray-800 text-green-400 px-4 py-2 rounded text-sm overflow-x-auto">
                POST {process.env.REACT_APP_BACKEND_URL}/api/send-feedback-emails
              </code>
              <p className="text-xs text-gray-500 mt-3">
                Configurez un service externe comme <a href="https://cron-job.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">cron-job.org</a> pour 
                appeler cet endpoint toutes les heures. Les emails seront envoyés automatiquement aux 
                clients 24h après leur visite.
              </p>
            </div>
          </div>
        </div>

        {/* Status Info */}
        {settings.updated_at && (
          <p className="text-xs text-gray-500 text-center mt-4">
            Dernière modification : {new Date(settings.updated_at).toLocaleDateString('fr-FR', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </p>
        )}
      </main>
    </div>
  );
};

export default AdminFeedbackSettings;
